// backbonecraftco-com#52: distributed Build-A-Box picker. Replaces the
// single checkbox-list picker page (build-a-box-picker.js) with a button
// on each real eligible product's own page (build-a-box-button.liquid)
// plus this one global module, loaded once site-wide from layout/theme.liquid,
// that owns:
//   - the one context fetch (which items are eligible, min/max, pricing,
//     the selling plan each pick gets added with -- see #73), cached per
//     browser session so every page load isn't a new request
//   - the running selection itself, in localStorage so it survives
//     navigating between eligible products' real pages
//   - the floating box bar + drawer (this file renders that UI directly --
//     it's dynamic, session-dependent content, not something worth a
//     separate server-rendered snippet)
//   - wiring up whichever per-product button exists on the current page
//
// One shared module instead of two separate ones specifically so there's
// a single source of truth for the fetched context and the selection --
// two independent copies polling/writing the same localStorage key is a
// real way to introduce race conditions for no benefit.

const PROXY_BASE_URL = 'https://subscriptions.backbonecraftco.com/account-proxy';
const CONTEXT_CACHE_KEY = 'bab:context:v1';

let contextPromise = null;

function getContext() {
  if (contextPromise) return contextPromise;

  contextPromise = (async () => {
    const cached = sessionStorage.getItem(CONTEXT_CACHE_KEY);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        // fall through to a real fetch on a corrupt cache entry
      }
    }

    try {
      const res = await fetch(`${PROXY_BASE_URL}/build-a-box-picker-active`);
      const body = await res.json();
      if (!res.ok || body.status !== 'ok') return null;
      sessionStorage.setItem(CONTEXT_CACHE_KEY, JSON.stringify(body));
      return body;
    } catch {
      return null;
    }
  })();

  return contextPromise;
}

function selectionKey(groupId) {
  return `bab:selection:${groupId}`;
}

function getSelection(groupId) {
  try {
    const raw = localStorage.getItem(selectionKey(groupId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function setSelection(groupId, items) {
  localStorage.setItem(selectionKey(groupId), JSON.stringify(items));
  document.dispatchEvent(new CustomEvent('build-a-box:change'));
}

/** Returns {ok, full} -- full means "already at max_items and this
 * item wasn't already in the box", the one case a toggle can't just
 * apply silently. */
function toggleItem(context, itemCode) {
  const selection = getSelection(context.group_id);
  const idx = selection.indexOf(itemCode);
  if (idx >= 0) {
    selection.splice(idx, 1);
    setSelection(context.group_id, selection);
    return { ok: true, full: false };
  }
  if (selection.length >= context.max_items) {
    return { ok: false, full: true };
  }
  selection.push(itemCode);
  setSelection(context.group_id, selection);
  return { ok: true, full: false };
}

function formatMoney(amount) {
  return `$${amount.toFixed(2)}`;
}

/** Mirrors compute_price's own tiering (subscriptions_build_a_box.py) --
 * cheapest items count toward the included tier first. Since #73, this
 * is what checkout actually bills too -- each pick is its own real cart
 * line and build-a-box-discount (a real Shopify Function) applies this
 * exact tiering for real, at the real cart lines, not just a preview. */
function pricePreview(context, selection) {
  const chosen = context.eligible_items
    .filter((item) => selection.includes(item.item_code))
    .sort((a, b) => a.price - b.price);

  let total = 0;
  chosen.forEach((item, idx) => {
    const pct = idx < context.min_items ? context.included_item_discount_percent : item.extra_item_discount_percent || 0;
    total += item.price * (1 - pct / 100);
  });
  return total;
}

// ── Floating bar + drawer ──

function ensureBar(context) {
  let bar = document.getElementById('build-a-box-bar');
  if (bar) return bar;

  bar = document.createElement('div');
  bar.id = 'build-a-box-bar';
  bar.innerHTML = `
    <button type="button" class="build-a-box-bar__toggle" aria-expanded="false" aria-controls="build-a-box-drawer">
      <span class="build-a-box-bar__count" aria-hidden="true"></span>
      <span class="build-a-box-bar__label">Your Build-A-Box</span>
    </button>
    <div id="build-a-box-drawer" class="build-a-box-drawer" hidden>
      <div class="build-a-box-drawer__header">
        <h2 class="build-a-box-drawer__heading">Your Build-A-Box</h2>
        <button type="button" class="build-a-box-drawer__close" aria-label="Close">&times;</button>
      </div>
      <p class="build-a-box-drawer__hint"></p>
      <ul class="build-a-box-drawer__items" role="list"></ul>
      <p class="build-a-box-drawer__total" aria-live="polite"></p>
      <p class="build-a-box-drawer__error" role="alert" hidden></p>
      <button type="button" class="build-a-box-drawer__submit button">Add to Subscription</button>
    </div>
  `;
  document.body.appendChild(bar);

  const toggleBtn = bar.querySelector('.build-a-box-bar__toggle');
  const drawer = bar.querySelector('.build-a-box-drawer');
  const closeBtn = bar.querySelector('.build-a-box-drawer__close');
  const submitBtn = bar.querySelector('.build-a-box-drawer__submit');
  const errorEl = bar.querySelector('.build-a-box-drawer__error');

  function setOpen(open) {
    drawer.hidden = !open;
    toggleBtn.setAttribute('aria-expanded', String(open));
    if (open) drawer.querySelector('.build-a-box-drawer__close')?.focus();
  }

  toggleBtn.addEventListener('click', () => setOpen(drawer.hidden));
  closeBtn.addEventListener('click', () => setOpen(false));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !drawer.hidden) setOpen(false);
  });

  submitBtn.addEventListener('click', async () => {
    const selection = getSelection(context.group_id);
    errorEl.hidden = true;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Adding...';
    try {
      // backbone-craftcore-erpnext#73: each pick is its own real cart
      // line now (its own real variant + this box's selling plan), not
      // one container line carrying a JSON property -- this is what
      // lets build-a-box-discount apply the real tiered discount against
      // real per-line prices instead of trusting client-supplied data.
      // Every one of these variants has to already be opted into the
      // selling plan (sellingPlanGroupAddProductVariants) or Shopify
      // rejects the add with "Cannot apply selling plan to variant" --
      // same error the container-only version of this hit before #73.
      const items = selection
        .map((itemCode) => context.eligible_items.find((item) => item.item_code === itemCode))
        .filter(Boolean)
        .map((item) => ({
          id: Number(item.shopify_variant_id),
          quantity: 1,
          selling_plan: Number(context.container_shopify_selling_plan_id),
        }));

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({items}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.description || 'Could not add to cart.');
      }
      localStorage.removeItem(selectionKey(context.group_id));
      window.location.href = '/cart';
    } catch (err) {
      errorEl.textContent = err.message || 'Something went wrong -- please try again.';
      errorEl.hidden = false;
      submitBtn.disabled = false;
      submitBtn.textContent = 'Add to Subscription';
    }
  });

  return bar;
}

function renderBar(context) {
  const selection = getSelection(context.group_id);
  const bar = document.getElementById('build-a-box-bar');

  if (!selection.length) {
    if (bar) bar.hidden = true;
    return;
  }

  const el = bar || ensureBar(context);
  el.hidden = false;

  const inRange = selection.length >= context.min_items && selection.length <= context.max_items;
  const countEl = el.querySelector('.build-a-box-bar__count');
  countEl.textContent = String(selection.length);

  const hintEl = el.querySelector('.build-a-box-drawer__hint');
  hintEl.textContent = inRange
    ? `${selection.length} selected -- ready to add (${context.min_items}-${context.max_items} allowed).`
    : `${selection.length} selected -- pick at least ${context.min_items} to continue (up to ${context.max_items}).`;

  const listEl = el.querySelector('.build-a-box-drawer__items');
  listEl.innerHTML = '';
  selection.forEach((itemCode) => {
    const item = context.eligible_items.find((i) => i.item_code === itemCode);
    if (!item) return;
    const li = document.createElement('li');
    li.className = 'build-a-box-drawer__item';
    let label;
    if (item.product_handle) {
      label = document.createElement('a');
      label.href = `/products/${item.product_handle}`;
      label.className = 'build-a-box-drawer__item-link';
    } else {
      label = document.createElement('span');
    }
    label.textContent = `${item.title} — ${formatMoney(item.price)}`;
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'build-a-box-drawer__remove';
    removeBtn.setAttribute('aria-label', `Remove ${item.title} from your Build-A-Box`);
    removeBtn.textContent = '×';
    removeBtn.addEventListener('click', () => {
      toggleItem(context, itemCode);
      document.querySelectorAll(`.build-a-box-button[data-item-code="${itemCode}"]`).forEach(updateButtonState);
    });
    li.append(label, removeBtn);
    listEl.appendChild(li);
  });

  const total = pricePreview(context, selection);
  const totalEl = el.querySelector('.build-a-box-drawer__total');
  totalEl.textContent = inRange
    ? `${formatMoney(total)}/period with your picks' discount applied.`
    : '';

  el.querySelector('.build-a-box-drawer__submit').disabled = !inRange;
}

// ── Per-product button ──

function updateButtonState(button) {
  const itemCode = button.dataset.itemCode;
  getContext().then((context) => {
    if (!context || context.status !== 'ok') {
      button.hidden = true;
      return;
    }
    const eligible = context.eligible_items.some((i) => i.item_code === itemCode || i.shopify_variant_id === button.dataset.variantId);
    if (!eligible) {
      button.hidden = true;
      return;
    }
    // Resolve the real item_code by variant id if this button was rendered
    // before we knew which variant is selected (see variant:update below).
    const match = context.eligible_items.find((i) => i.shopify_variant_id === button.dataset.variantId);
    if (match) button.dataset.itemCode = match.item_code;

    button.hidden = false;
    const inBox = getSelection(context.group_id).includes(button.dataset.itemCode);
    button.classList.toggle('build-a-box-button--in-box', inBox);
    button.querySelector('.build-a-box-button__label').textContent = inBox
      ? 'In Your Build-A-Box ✓'
      : 'Add to Build A Box Subscription';
  });
}

function initButton(button) {
  updateButtonState(button);

  button.addEventListener('click', async () => {
    const context = await getContext();
    if (!context || context.status !== 'ok') return;
    const errorEl = button.parentElement.querySelector('.build-a-box-button__error');
    const result = toggleItem(context, button.dataset.itemCode);
    if (!result.ok && result.full) {
      if (errorEl) {
        errorEl.textContent = `Your box is full (max ${context.max_items}) -- remove an item to add another.`;
        errorEl.hidden = false;
      }
      return;
    }
    if (errorEl) errorEl.hidden = true;
    updateButtonState(button);
  });

  // The theme's variant picker swaps the selected variant without a full
  // page reload (variant:update) -- re-check eligibility against whatever
  // variant is now selected rather than staying pinned to the variant that
  // was selected at page load.
  document.addEventListener('variant:update', (event) => {
    const newVariantId = event.detail?.resource?.id;
    if (!newVariantId) return;
    button.dataset.variantId = String(newVariantId);
    updateButtonState(button);
  });
}

document.addEventListener('build-a-box:change', () => {
  getContext().then((context) => {
    if (context && context.status === 'ok') renderBar(context);
  });
  document.querySelectorAll('.build-a-box-button').forEach(updateButtonState);
});

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.build-a-box-button').forEach(initButton);
  getContext().then((context) => {
    if (context && context.status === 'ok') renderBar(context);
  });
});
